import styled from "styled-components";
import Hero from "src/components/Hero/Hero";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import EventFinished from "src/components/EventFinished";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

const IMAGE_BASE = "promotions/gaming-machines/rcg-cruise-november-2026";

const RcgCruiseNovember2026 = () => {
  const { t, i18n } = useTranslation();
  const parallaxImage = useResponsiveImageUrl(
    `${IMAGE_BASE}/rc-cruise-parallax.jpg`,
  );

  return (
    <Div>
      <Hero
        bgImage={`${IMAGE_BASE}/rc-hero-image-2400x1150px.jpg`}
        image={`${IMAGE_BASE}/rc-logo-2400x900px-${i18n.language}.png`}
        imageAlt={t("rcgCruiseNovember2026.hero.logoAlt")}
        title={t("rcgCruiseNovember2026.hero.title")}
        description={t("rcgCruiseNovember2026.hero.subtitle")}
        descriptionBgColor="rgba(197, 157, 103, 0.85)"
        className="rcg-hero"
        fluidImage
      />
      <EventFinished
        scheduleKeyName="rcgCruiseNovember2026"
        eventType="promotion"
      />
      <MainContentDiv>
        <SectionsWrapper>
          <ContentBand>
          <BlueSection bgImage={parallaxImage}>
            <div className="sections-inner">
              <SubTitleSection
                subTitle={t("rcgCruiseNovember2026.content.sectionTitle")}
                text={t("rcgCruiseNovember2026.content.sectionText")}
                borderColor="#c5b48a"
                dark={false}
                as="h2"
                subtitleColor="#fff"
                className="blue-title"
              />
            </div>
          </BlueSection>
          <ImageWrapper>
            <ResponsiveImage
              src={`${IMAGE_BASE}/rc-cruise-photos-november.png`}
              alt={t("rcgCruiseNovember2026.content.sectionTitle")}
              customStyles={{
                width: "100%",
                height: "auto",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </ImageWrapper>
          <DetailsSection>
            <div className="sections-inner">
              <SubTitleSection
                subTitle={t("rcgCruiseNovember2026.content.detailsTitle")}
                borderColor="#c5b48a"
                dark={false}
                as="h2"
                subtitleColor="#fff"
                className="details-title"
              />
              <div className="details-grid">
                <div>
                  <span className="label">
                    {t("rcgCruiseNovember2026.content.details.whereLabel")}
                  </span>
                  <p>{t("rcgCruiseNovember2026.content.details.whereText")}</p>
                </div>
                <div>
                  <span className="label">
                    {t("rcgCruiseNovember2026.content.details.whenLabel")}
                  </span>
                  <p>{t("rcgCruiseNovember2026.content.details.whenText")}</p>
                </div>
                <div>
                  <span className="label">
                    {t("rcgCruiseNovember2026.content.details.timeLabel")}
                  </span>
                  <p>{t("rcgCruiseNovember2026.content.details.timeText")}</p>
                </div>
              </div>
            </div>
          </DetailsSection>
          </ContentBand>
          <container-div>
            <ReactMarkdown className="rsvp-note">
              {t("rcgCruiseNovember2026.content.rsvp")}
            </ReactMarkdown>
          </container-div>
        </SectionsWrapper>
      </MainContentDiv>
    </Div>
  );
};

const Div = styled.div`
  width: 100%;
  height: auto;

  .hero_container {
    margin-bottom: 120px;
    background-position: bottom;
    overflow: visible;
  }

  .rcg-hero {
    .hero_image_container {
      padding: 20px 20px 40px;
      min-height: 900px;
      justify-content: flex-start;
    }

    .hero_image {
      max-width: 820px;
      margin-top: 140px;
      width: 100%;
    }

    .hero_text_container {
      position: relative;
      bottom: -120px;
      background-color: #b8a47d;
      backdrop-filter: none;
      padding: 40px 20px;
      z-index: 2;
    }

    .hero_text_container h1 {
      margin-bottom: 20px;
      color: #fff;
    }

    .hero_text_container p {
      margin: 0;
      color: #fff;
    }
  }

  @media (max-width: 991px) {
    .hero_container {
      margin-bottom: 180px;
    }

    .rcg-hero {
      .hero_image_container {
        min-height: 400px;
        padding: 20px;
      }

      .hero_text_container {
        bottom: -180px;
      }
    }
  }
`;

const MainContentDiv = styled.div`
  background-color: #030508;
  width: 100%;
`;

const SectionsWrapper = styled.div`
  position: relative;
  width: 100%;
  background-color: #030508;
  padding-bottom: 40px;

  .subtitle-section {
    h2::before {
      width: fit-content;
    }
  }

  .rsvp-note {
    margin: 40px auto;
    padding: 0;
    text-align: center;
    font-size: 1.05rem;
    font-style: italic;
    color: rgba(255, 255, 255, 0.88);
  }

  .rsvp-note a,
  .rsvp-note strong {
    color: #fff;
    text-decoration: none;
  }

  .rsvp-note a:hover,
  .rsvp-note a:focus {
    text-decoration: underline;
  }

  @media (max-width: 992px) {
    position: static;
  }
`;

const ContentBand = styled.div`
  position: relative;
`;

const BlueSection = styled.section`
  width: 100%;
  padding: 40px 0;
  position: relative;
  background-color: #061525;
  background-image: url(${(props) => props.bgImage});
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;

  .sections-inner {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  .blue-title {
    max-width: 42%;
    padding: 0;

    h2 {
      white-space: pre-line;
    }

    p,
    strong {
      color: #fff;
    }
  }

  @media (max-width: 992px) {
    padding: 40px 0;

    .sections-inner {
      padding: 0 20px;
    }

    .blue-title {
      max-width: 100%;
    }
  }
`;

const DetailsSection = styled.section`
  width: 100%;
  padding: 40px 0;
  position: relative;
  background-color: #030508;

  .sections-inner {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
  }

  .details-title {
    padding: 0 0 1.5rem;
    max-width: 42%;
  }

  .details-grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: 1fr;
    max-width: 42%;
  }

  .details-grid p {
    margin: 0.35rem 0 0;
    white-space: pre-line;
    color: rgba(255, 255, 255, 0.88);
  }

  .label {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.85rem;
    letter-spacing: 0.08em;
    color: #e8d7b0;
    display: block;
  }

  @media (max-width: 992px) {
    padding: 40px 0;

    .sections-inner {
      padding: 0 20px;
    }

    .details-title,
    .details-grid {
      max-width: 100%;
    }
  }
`;

const ImageWrapper = styled.div`
  --text-end: calc(
    (100vw - min(100vw, 1400px)) / 2 + 2rem + (min(100vw, 1400px) - 4rem) * 0.42 +
      1.25rem
  );
  --img-width: min(
    560px,
    calc(100vw - var(--text-end) - 1.2rem),
    calc(420px + max(0px, 100vw - 1000px) * 0.22)
  );
  position: absolute;
  top: 55%;
  right: max(1.2rem, calc(100vw - var(--text-end) - var(--img-width)));
  transform: translateY(-50%);
  width: var(--img-width);
  max-height: 88%;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  img {
    width: 100%;
    max-height: 100%;
    height: auto;
    object-fit: contain;
  }

  @media (min-width: 1800px) {
    right: max(1.2rem, calc((100vw - 1400px) / 2 + 5rem));
  }

  @media (max-width: 992px) {
    position: static;
    transform: none;
    width: 100%;
    max-width: 100%;
    max-height: none;
    margin: 0;
    padding: 0;

    img {
      width: 100%;
      max-height: none;
      margin: 0 auto;
    }
  }
`;

export default RcgCruiseNovember2026;
